import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { Skeleton } from "../components/Skeleton";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const RECOMMENDATION_COLORS = {
  hot: "#3ddc97",
  good: "#a394ff",
  neutral: "#f0b84a",
  avoid: "#ff6b7a",
};

const RECOMMENDATION_LABELS = {
  hot: "Hot",
  good: "Good value",
  neutral: "Neutral",
  avoid: "Risky",
};

export function Machines() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["machine-analysis"],
    queryFn: async () => {
      const { data, error } = await api.machines.analysis.get();
      if (error) throw error;
      return data;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const chartData =
    data?.data
      .filter((m) => m.evRatio !== null)
      .map((m) => ({
        name: m.name.replace("Pokemon ", "").replace(" Gacha", ""),
        evRatio: Math.round((m.evRatio ?? 0) * 1000) / 10,
        recommendation: m.recommendation,
      })) ?? [];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="page-title">Machines</h1>
        <p className="section-hint mt-1">
          Compare gacha machines by expected value and find the best odds.
        </p>
      </div>

      {/* Buyback Rate Info */}
      <section className="card">
        <h2 className="section-title mb-3">Instant buyback tiers</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="card-inset p-4 text-center">
            <div className="num text-3xl font-semibold text-gray-50">85%</div>
            <div className="text-sm text-gray-400 mt-1">$25 to $99 machines</div>
          </div>
          <div className="card-inset p-4 text-center">
            <div className="num text-3xl font-semibold text-gray-50">90%</div>
            <div className="text-sm text-gray-400 mt-1">$100 to $500 machines</div>
          </div>
          <div className="card-inset p-4 text-center">
            <div className="num text-3xl font-semibold text-gray-50">93%</div>
            <div className="text-sm text-gray-400 mt-1">$1000+ machines</div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Buyback is the guaranteed return if your prize is worth less than
          your spin cost.
        </p>
      </section>

      {/* EV Chart */}
      {chartData.length > 0 && (
        <section className="card">
          <h2 className="section-title mb-4">Expected value ratio by machine</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2739" />
                <XAxis
                  type="number"
                  stroke="#5b6982"
                  tick={{ fill: "#8b98b0", fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, (max: number) => Math.max(120, Math.ceil(max / 20) * 20)]}
                  tickFormatter={(v) => `${v}%`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="#5b6982"
                  tick={{ fill: "#8b98b0", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={80}
                />
                <Tooltip
                  cursor={{ fill: "#1f2739" }}
                  contentStyle={{
                    backgroundColor: "#131826",
                    border: "1px solid #2c374d",
                    borderRadius: 10,
                  }}
                  labelStyle={{ color: "#8b98b0" }}
                  itemStyle={{ color: "#eaeef6" }}
                  formatter={(value) => [`${value}%`, "EV ratio"]}
                />
                <Bar dataKey="evRatio" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        RECOMMENDATION_COLORS[
                          entry.recommendation as keyof typeof RECOMMENDATION_COLORS
                        ]
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-4 mt-4 justify-center flex-wrap">
            {Object.entries(RECOMMENDATION_LABELS).map(([key, label]) => (
              <div key={key} className="flex items-center gap-2 text-sm">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{
                    backgroundColor:
                      RECOMMENDATION_COLORS[
                        key as keyof typeof RECOMMENDATION_COLORS
                      ],
                  }}
                />
                <span className="text-gray-400">{label}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Machine List */}
      <section className="card">
        <h2 className="section-title mb-4">All machines</h2>

        {isLoading && <Skeleton kind="table" rows={10} />}
        {error && (
          <p className="text-red-400">
            Couldn't load machine data. Try again in a minute.
          </p>
        )}

        {data?.data && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="table-head">
                  <th className="pb-3 pr-4 font-medium">Machine</th>
                  <th className="pb-3 pr-4 font-medium">Price</th>
                  <th className="pb-3 pr-4 font-medium">Buyback</th>
                  <th className="pb-3 pr-4 font-medium">Stock</th>
                  <th className="pb-3 pr-4 font-medium">Wins 24h</th>
                  <th className="pb-3 pr-4 font-medium">Avg prize</th>
                  <th className="pb-3 pr-4 font-medium">EV ratio</th>
                  <th className="pb-3 font-medium">Rating</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((machine) => (
                  <tr key={machine.code} className="table-row">
                    <td className="py-3 pr-4">
                      <div className="font-medium text-gray-100">
                        {machine.name}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">
                        {machine.code}
                      </div>
                    </td>
                    <td className="py-3 pr-4 num text-gray-300">
                      ${machine.price}
                    </td>
                    <td className="py-3 pr-4 num text-gray-300">
                      {(machine.buybackRate * 100).toFixed(0)}%
                    </td>
                    <td className="py-3 pr-4">
                      <div className="num text-gray-300">{machine.stock.total}</div>
                      <div className="num text-xs text-gray-500">
                        {machine.stock.common}c {machine.stock.uncommon}u{" "}
                        {machine.stock.rare}r {machine.stock.epic}e
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`num ${
                          machine.recentWins24h >= 10
                            ? "text-green-400 font-medium"
                            : "text-gray-300"
                        }`}
                      >
                        {machine.recentWins24h}
                      </span>
                    </td>
                    <td className="py-3 pr-4 num text-gray-300">
                      {machine.avgPrizeValue !== null
                        ? `$${machine.avgPrizeValue.toFixed(0)}`
                        : "–"}
                    </td>
                    <td className="py-3 pr-4">
                      {machine.evRatio !== null ? (
                        <span
                          className={`num ${
                            machine.evRatio >= 1
                              ? "text-green-400 font-medium"
                              : machine.evRatio >= 0.9
                              ? "text-blue-400"
                              : "text-gray-300"
                          }`}
                        >
                          {(machine.evRatio * 100).toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-gray-500">–</span>
                      )}
                    </td>
                    <td className="py-3">
                      <span
                        className="pill"
                        style={{
                          backgroundColor: `${
                            RECOMMENDATION_COLORS[machine.recommendation]
                          }26`,
                          color: RECOMMENDATION_COLORS[machine.recommendation],
                        }}
                      >
                        {RECOMMENDATION_LABELS[machine.recommendation]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data?.data?.length === 0 && (
          <p className="text-gray-500">
            No machine data yet. Machines appear once Collector Crypt's status
            feed is reachable.
          </p>
        )}
      </section>

      {/* Legend */}
      <section className="card">
        <h3 className="text-sm font-medium text-gray-200 mb-2">
          How to read the ratings
        </h3>
        <ul className="text-sm text-gray-400 space-y-1">
          <li>
            <span className="text-green-400 font-medium">EV ratio over 100%</span>
            : expected to profit on average.
          </li>
          <li>
            <span className="text-blue-400 font-medium">EV ratio 92 to 100%</span>
            : good value, close to break-even.
          </li>
          <li>
            <span className="text-yellow-400 font-medium">EV ratio 85 to 92%</span>
            : neutral, buyback covers most of the loss.
          </li>
          <li>
            <span className="text-red-400 font-medium">EV ratio under 85%</span>
            : higher risk, below buyback protection.
          </li>
        </ul>
      </section>
    </div>
  );
}
